<template>
  <div class="help-page">
    <h4 class="table-page-header">Help Center</h4>

    <div class="help-layout">
      <!-- Topic list -->
      <div class="help-sidebar" :class="{ open: sidebarOpen }">
        <el-input
          v-model="search"
          placeholder="Search help topics..."
          clearable
          class="help-search"
        >
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>

        <div v-for="section in visibleSections" :key="section.name" class="help-section">
          <div class="help-section-title">{{ section.name }}</div>
          <div
            v-for="article in section.articles"
            :key="article.id"
            class="help-topic"
            :class="{ active: article.id === activeId }"
            @click="selectArticle(article.id)"
          >
            {{ article.title }}
          </div>
          <div
            v-for="planned in section.planned"
            :key="'p-' + planned.title"
            class="help-topic planned"
            title="This guide hasn't been written yet"
          >
            {{ planned.title }}
            <span class="soon-badge">soon</span>
          </div>
        </div>

        <div v-if="search && visibleSections.length === 0" class="help-no-results">
          No topics match "{{ search }}"
        </div>
      </div>

      <!-- Article content -->
      <div class="help-content">
        <button class="help-topics-toggle" @click="sidebarOpen = !sidebarOpen">
          <i class="fas fa-list"></i> {{ sidebarOpen ? 'Hide topics' : 'All topics' }}
        </button>
        <div v-if="activeArticle" class="help-article">
          <h2>{{ activeArticle.title }}</h2>
          <!-- eslint-disable-next-line vue/no-v-html -- our own bundled markdown, no user input -->
          <div class="help-article-body" v-html="renderedBody"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from "marked";
import { ARTICLES, PLANNED, SECTIONS } from "@/help";

export default {
  name: "HelpPage",
  data() {
    return {
      search: "",
      activeId: this.$route.params.articleId || (ARTICLES[0] && ARTICLES[0].id),
      sidebarOpen: false,
    };
  },
  computed: {
    visibleSections() {
      const q = this.search.trim().toLowerCase();
      const match = (a) =>
        !q ||
        a.title.toLowerCase().includes(q) ||
        (a.keywords || "").toLowerCase().includes(q) ||
        (a.body || "").toLowerCase().includes(q);
      return SECTIONS.map((name) => ({
        name,
        articles: ARTICLES.filter((a) => a.section === name && match(a)),
        // Hide planned entries while searching — they have no content yet
        planned: q ? [] : PLANNED.filter((p) => p.section === name),
      })).filter((s) => s.articles.length > 0 || s.planned.length > 0);
    },
    activeArticle() {
      return ARTICLES.find((a) => a.id === this.activeId) || ARTICLES[0];
    },
    renderedBody() {
      return this.activeArticle ? marked.parse(this.activeArticle.body) : "";
    },
  },
  watch: {
    "$route.params.articleId"(id) {
      if (id && ARTICLES.some((a) => a.id === id)) this.activeId = id;
    },
  },
  methods: {
    selectArticle(id) {
      this.activeId = id;
      this.sidebarOpen = false;
      if (this.$route.params.articleId !== id) {
        this.$router.replace({ path: `/pages/help/${id}` });
      }
      const content = this.$el.querySelector(".help-content");
      if (content) content.scrollTop = 0;
      window.scrollTo({ top: 0 });
    },
  },
};
</script>

<style scoped>
/* Same theme block the other pages define (Shifts/Calendar/Reports) */
.help-page {
  --primary-color: #6ba6cd;
  --primary-hover: #5a94b8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  color: var(--text-primary);
}

.help-layout {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
}

.help-sidebar {
  flex: 0 0 280px;
  background: var(--background-glass);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-subtle);
  border-radius: 10px;
  padding: 1rem;
  position: sticky;
  top: 1rem;
}

.help-search {
  margin-bottom: 0.75rem;
}

.help-section-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin: 0.9rem 0 0.35rem;
}

.help-topic {
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1.3;
  color: var(--text-primary);
}

.help-topic:hover {
  background: var(--background-glass);
}

.help-topic.active {
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
}

.help-topic.planned {
  color: var(--text-secondary);
  cursor: default;
}

.soon-badge {
  font-size: 0.65rem;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 0 0.35rem;
  margin-left: 0.3rem;
  vertical-align: 1px;
}

.help-no-results {
  color: var(--text-secondary);
  font-size: 0.85rem;
  padding: 0.5rem;
}

.help-content {
  flex: 1;
  min-width: 0;
}

.help-topics-toggle {
  display: none;
  background: var(--background-glass);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  border-radius: 6px;
  padding: 0.35rem 0.7rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
}

.help-topics-toggle:hover {
  border-color: var(--primary-color);
}

.help-article {
  background: var(--background-glass);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-subtle);
  border-radius: 10px;
  padding: 1.5rem 1.75rem 1.75rem;
  max-width: 860px;
}

.help-article h2 {
  margin: 0 0 0.75rem;
  font-size: 1.4rem;
  color: var(--text-primary);
}

.help-article-body {
  line-height: 1.65;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.help-article-body :deep(h2) {
  font-size: 1.1rem;
  margin: 1.4rem 0 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--primary-color);
}

.help-article-body :deep(ul),
.help-article-body :deep(ol) {
  padding-left: 1.4rem;
  margin: 0.5rem 0;
}

.help-article-body :deep(li) {
  margin-bottom: 0.3rem;
}

.help-article-body :deep(strong) {
  color: #fff;
}

.help-article-body :deep(blockquote) {
  border-left: 3px solid var(--primary-color);
  margin: 0.75rem 0;
  padding: 0.4rem 0.9rem;
  background: var(--background-glass);
  border-radius: 0 6px 6px 0;
}

.help-article-body :deep(blockquote p) {
  margin: 0;
}

.help-article-body :deep(table) {
  border-collapse: collapse;
  margin: 0.75rem 0;
}

.help-article-body :deep(th),
.help-article-body :deep(td) {
  border: 1px solid var(--border-color);
  padding: 0.35rem 0.7rem;
  text-align: left;
}

.help-article-body :deep(th) {
  background: var(--background-glass);
  color: var(--text-primary);
}

@media (max-width: 991px) {
  .help-layout {
    flex-direction: column;
  }

  .help-sidebar {
    display: none;
    position: static;
    width: 100%;
    flex: none;
  }

  .help-sidebar.open {
    display: block;
  }

  .help-topics-toggle {
    display: inline-block;
  }

  .help-content {
    width: 100%;
  }

  .help-article {
    padding: 1rem 1.1rem 1.25rem;
  }
}
</style>
